import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import Moveable from "react-moveable";
import Selecto from "react-selecto";
import { Table, CombinedTable, Reservation } from "@/types/table";
import { useFloorPlanV2, V2Position } from "@/hooks/useFloorPlanV2";
import { useFloorPlanZones } from "@/hooks/useFloorPlanZones";
import { useFloorPlanAllView } from "@/hooks/useFloorPlanAllView";
import { V2Toolbar } from "./V2Toolbar";
import { V2Table } from "./V2Table";
import { V2TableDroppable } from "./V2TableDroppable";
import { V2ServiceBar } from "./V2ServiceBar";
import { V2Minimap } from "./V2Minimap";
import { V2ReservationPanel } from "./V2ReservationPanel";
import { FloorPlanDndProvider, ReservationDragItem } from "./FloorPlanDndProvider";
import { ReservationDragLayer } from "./ReservationDragLayer";
import { ForceDropDialog } from "./ForceDropDialog";
import { validateReservationDrop } from "@/lib/floorPlanDropValidation";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { Eye, Pencil, ListOrdered, Search, Rows3, Rows } from "lucide-react";
import { V2TableSearchPalette } from "./V2TableSearchPalette";
import { V2ZoneFilterSelect } from "./V2ZoneFilterSelect";
import { V2Fixture } from "./V2Fixture";
import { useFloorPlanFixtures } from "@/hooks/useFloorPlanFixtures";
import { getTableStatus } from "@/lib/floorPlanUtils";
import { parseTimeToMinutes } from "@/lib/timeBookingUtils";
import { supabase } from "@/integrations/supabase/client";
import { FloorPlanDragProvider, useFloorPlanDrag } from "@/contexts/FloorPlanDragContext";
import { TableSwapConfirmDialog } from "@/components/TableSwapConfirmDialog";
import { toast } from "sonner";
import { globalFromLocal, localFromGlobal, localRotFromGlobal, type AllViewZoneLite } from "@/lib/floorPlanAllViewMath";
import { useServiceTimeCompare } from "@/hooks/useServiceTimeCompare";
import { serviceSortMinutes } from "@/lib/serviceOrder";

interface V2TimeSlot { start: string; end: string }

interface Props {
  establishmentId: string;
  tables: Table[];
  // Service mode (optional — without these, only edit mode works)
  combinedTables?: CombinedTable[];
  reservations?: Reservation[];
  selectedDate?: Date;
  timeSlots?: V2TimeSlot[];
  onDateChange?: (d: Date) => void;
  onCreateAtTable?: (tableId: string, time: string, date: Date) => void;
  onEditReservation?: (reservation: Reservation) => void;
  /** When defined, a click on an occupied table opens the preview instead of going straight to edit. */
  onPreviewReservation?: (reservation: Reservation) => void;
  onStatusChange?: (reservation: Reservation, status: "arrivé" | "annulé" | "planifié") => void;
  onTableChange?: (reservationId: string, tableId: string | null, combinedTableId: string | null) => void;
  onLockChange?: (reservationId: string, locked: boolean) => void;
  onSwapTables?: (reservationAId: string, reservationBId: string) => Promise<void> | void;
  reservationSettings?: { bookingMode: 'slot' | 'time'; reservationDurationMinutes: number };
}

const SNAP = 8;
const MIN_SIZE = 32;
const CANVAS_MIN_W = 1200;
const CANVAS_MIN_H = 800;
const CANVAS_MARGIN = 240;
const CANVAS_SHRINK_THRESHOLD = 320;

type V2Mode = 'service' | 'edit';

// Public wrapper: provides FloorPlanDragContext + react-dnd backend so the
// reservation panel + canvas can share drag state on both mouse and touch.
export const FloorPlanV2 = (props: Props) => (
  <FloorPlanDndProvider>
    <FloorPlanDragProvider>
      <FloorPlanV2Inner {...props} />
    </FloorPlanDragProvider>
  </FloorPlanDndProvider>
);

const FloorPlanV2Inner = ({
  establishmentId, tables,
  combinedTables = [], reservations = [], selectedDate,
  timeSlots = [], onDateChange, onCreateAtTable, onEditReservation, onPreviewReservation,
  onStatusChange, onTableChange, onLockChange, onSwapTables,
  reservationSettings,
}: Props) => {
  const compareServiceTime = useServiceTimeCompare();
  const { zones, defaultZone } = useFloorPlanZones({ establishmentId });
  const [selectedZoneId, setSelectedZoneId] = useState<string | null>(null);

  // === Mode (service vs edit) ===
  // Service mode must remain available even on dates without active slots
  // (e.g. La Tour on Sundays), otherwise managers cannot see/toggle V2.
  const isServiceCapable = !!(reservations && selectedDate && onDateChange);
  const [mode, setMode] = useState<V2Mode>(() => {
    if (!isServiceCapable) return 'edit';
    const stored = typeof window !== 'undefined' ? localStorage.getItem(`fp-v2-mode-${establishmentId}`) : null;
    return (stored === 'edit' || stored === 'service') ? stored : 'service';
  });
  useEffect(() => {
    localStorage.setItem(`fp-v2-mode-${establishmentId}`, mode);
  }, [mode, establishmentId]);
  useEffect(() => {
    if (!isServiceCapable && mode === 'service') setMode('edit');
  }, [isServiceCapable, mode]);

  // === Étape 7 — compact density toggle (persisted) ===
  const [compact, setCompact] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem(`fp-v2-compact-${establishmentId}`) === '1';
  });
  useEffect(() => {
    localStorage.setItem(`fp-v2-compact-${establishmentId}`, compact ? '1' : '0');
  }, [compact, establishmentId]);

  // === Étape 8 — zone highlight filter (vue Toutes, mode service) ===
  // Empty set = all zones full opacity; non-empty = highlight selected, dim others.
  const [highlightedZoneIds, setHighlightedZoneIds] = useState<Set<string>>(() => {
    if (typeof window === 'undefined') return new Set();
    const raw = localStorage.getItem(`fp-v2-zone-filter-${establishmentId}`);
    if (!raw) return new Set();
    try { return new Set(JSON.parse(raw) as string[]); } catch { return new Set(); }
  });
  useEffect(() => {
    localStorage.setItem(
      `fp-v2-zone-filter-${establishmentId}`,
      JSON.stringify(Array.from(highlightedZoneIds)),
    );
  }, [highlightedZoneIds, establishmentId]);

  // === Étape 7 — search palette (⌘K / Ctrl+K / `/`) ===
  const [searchOpen, setSearchOpen] = useState(false);
  const [focusedTableId, setFocusedTableId] = useState<string | null>(null);
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const inField = !!target && (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      );
      if ((e.key === 'k' || e.key === 'K') && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setSearchOpen(o => !o);
        return;
      }
      if (e.key === '/' && !inField && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);
  // Auto-clear focus halo after 1.8s
  useEffect(() => {
    if (!focusedTableId) return;
    const t = setTimeout(() => setFocusedTableId(null), 1800);
    return () => clearTimeout(t);
  }, [focusedTableId]);

  // === Selected time slot (service mode) ===
  const slotStrings = useMemo(() => Array.from(new Set(timeSlots.map(s => s.start))), [timeSlots]);
  const slotEndMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of timeSlots) if (!m.has(s.start)) m.set(s.start, s.end);
    return m;
  }, [timeSlots]);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  useEffect(() => {
    if (selectedTime && slotStrings.includes(selectedTime)) return;
    if (slotStrings.length === 0) { setSelectedTime(null); return; }
    // pick closest to now
    const now = new Date();
    const nowMin = now.getHours() * 60 + now.getMinutes();
    let best = slotStrings[0];
    let bestDiff = Infinity;
    for (const t of slotStrings) {
      const [h, m] = t.split(':').map(Number);
      const diff = Math.abs(h * 60 + m - nowMin);
      if (diff < bestDiff) { bestDiff = diff; best = t; }
    }
    setSelectedTime(best);
  }, [slotStrings, selectedTime]);

  // Covers per slot (selected date, excludes cancelled) — fed to V2ServiceBar
  const slotCoversMap = useMemo(() => {
    const map = new Map<string, number>();
    if (!selectedDate) return map;
    const k = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`;
    for (const r of reservations) {
      if (!r.date || r.status === 'annulé') continue;
      const d = r.date instanceof Date ? r.date : new Date(r.date);
      const kk = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      if (kk !== k) continue;
      const t = r.startTime.substring(0, 5);
      map.set(t, (map.get(t) ?? 0) + r.numberOfPeople);
    }
    return map;
  }, [reservations, selectedDate]);


  // init zone selection
  // Vue par défaut : dernière vue persistée si valide, sinon la zone par défaut.
  // (Plus de forçage automatique sur "Toutes" — l'utilisateur garde le contrôle.)
  useEffect(() => {
    if (selectedZoneId === null && zones.length > 0) {
      const stored = localStorage.getItem(`fp-v2-default-view-${establishmentId}`)
        ?? localStorage.getItem(`fp-v2-zone-${establishmentId}`);
      const valid = stored && stored !== '__all__' && zones.find(z => z.id === stored);
      const fallback = defaultZone?.id ?? zones[0].id;
      setSelectedZoneId(valid ? stored! : fallback);

    }
  }, [zones, defaultZone, establishmentId, selectedZoneId, tables, mode]);

  useEffect(() => {
    if (selectedZoneId) {
      localStorage.setItem(`fp-v2-zone-${establishmentId}`, selectedZoneId);
      localStorage.setItem(`fp-v2-default-view-${establishmentId}`, selectedZoneId);
    }
  }, [selectedZoneId, establishmentId]);

  const effectiveZoneId = selectedZoneId;
  const isAllZones = effectiveZoneId === '__all__';
  const editingZoneId = isAllZones ? null : effectiveZoneId;
  const activeZone = zones.find(z => z.id === editingZoneId) || null;

  const {
    positions, loading, saving, dirty, lastSavedAt,
    snapshot, updatePosition, updateMany, undo, redo, canUndo, canRedo, save,
  } = useFloorPlanV2(tables, { establishmentId, zoneId: isAllZones ? null : editingZoneId });

  // Étape 8 — fixtures globaux (rendus en vue Toutes + par zone en édition)
  const { fixtures: allFixtures } = useFloorPlanFixtures({ establishmentId, zoneId: null });
  const fixturesByZone = useMemo(() => {
    const m = new Map<string, typeof allFixtures>();
    for (const f of allFixtures) {
      if (!f.zoneId) continue;
      const arr = m.get(f.zoneId) ?? [];
      arr.push(f);
      m.set(f.zoneId, arr);
    }
    return m;
  }, [allFixtures]);

  // "Toutes" composition (zone-level positions, rotations, dims)
  const allView = useFloorPlanAllView({ establishmentId, autoSave: true });
  const ALL_VIEW_PAD = 80;
  const isAllZonesEdit = selectedZoneId === '__all__' && mode === 'edit';
  // Drag state for zone-level drag in "Toutes" édition (handlers defined after `zoom`)
  const zoneDragRef = useRef<{ id: string | null; startX: number; startY: number; baseX: number; baseY: number }>({
    id: null, startX: 0, startY: 0, baseX: 0, baseY: 0,
  });
  const zoneResizeRef = useRef<{ id: string | null; startX: number; startY: number; baseW: number; baseH: number }>({
    id: null, startX: 0, startY: 0, baseW: 0, baseH: 0,
  });

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isDraggingTables, setIsDraggingTables] = useState(false);
  // Last zone interacted with in "Toutes" édition — target of keyboard shortcuts
  const [selectedAllZoneId, setSelectedAllZoneId] = useState<string | null>(null);
  // Space-bar temporary pan mode for the "Toutes" canvas
  const [spacePanActive, setSpacePanActive] = useState(false);

  // Étape 10 — sous-mode "Disposer les zones" (vue Toutes + Édition uniquement)
  // Désactivé par défaut → zones = repères passifs, tables éditables comme zone simple.
  const [arrangeZones, setArrangeZones] = useState(false);

  // Plein écran pour l'édition « Toutes » — overlay fixed inset-0
  const [fullscreenEdit, setFullscreenEdit] = useState(false);

  // Reset selection on zone/mode change to avoid stale Moveable targets
  useEffect(() => { setSelectedIds([]); setSelectedAllZoneId(null); setArrangeZones(false); setFullscreenEdit(false); }, [selectedZoneId, mode]);

  // Escape ferme le plein écran
  useEffect(() => {
    if (!fullscreenEdit) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setFullscreenEdit(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [fullscreenEdit]);

  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLDivElement>(null);
  const moveableRef = useRef<Moveable>(null);
  const panStateRef = useRef<{ active: boolean; startX: number; startY: number; startPanX: number; startPanY: number }>({
    active: false, startX: 0, startY: 0, startPanX: 0, startPanY: 0,
  });
  // Drag state — captured on dragStart / dragGroupStart, applied during drag
  const dragRef = useRef<{
    startClientX: number;
    startClientY: number;
    starts: Map<string, { x: number; y: number; w: number; h: number; rot: number }>;
  }>({ startClientX: 0, startClientY: 0, starts: new Map() });

  // === Zone-level drag/resize for "Toutes" édition (uses zoom defined above) ===
  const startZoneDrag = useCallback((e: React.PointerEvent, zoneId: string, baseX: number, baseY: number) => {
    e.stopPropagation();
    e.preventDefault();
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
    setSelectedAllZoneId(zoneId);
    allView.snapshot();
    zoneDragRef.current = { id: zoneId, startX: e.clientX, startY: e.clientY, baseX, baseY };
  }, [allView]);
  const onZoneDragPointerMove = useCallback((e: React.PointerEvent) => {
    const st = zoneDragRef.current;
    if (!st.id) return;
    const z = zoom || 1;
    allView.updateZonePos(st.id, st.baseX + (e.clientX - st.startX) / z, st.baseY + (e.clientY - st.startY) / z);
  }, [allView, zoom]);
  const endZoneDrag = useCallback((e: React.PointerEvent) => {
    if (zoneDragRef.current.id) {
      (e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId);
      zoneDragRef.current.id = null;
    }
  }, []);
  const startZoneResize = useCallback((e: React.PointerEvent, zoneId: string, baseW: number, baseH: number) => {
    e.stopPropagation();
    e.preventDefault();
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
    allView.snapshot();
    zoneResizeRef.current = { id: zoneId, startX: e.clientX, startY: e.clientY, baseW, baseH };
  }, [allView]);
  const onZoneResizePointerMove = useCallback((e: React.PointerEvent) => {
    const st = zoneResizeRef.current;
    if (!st.id) return;
    const z = zoom || 1;
    allView.updateZoneCanvas(st.id, st.baseW + (e.clientX - st.startX) / z, st.baseH + (e.clientY - st.startY) / z);
  }, [allView, zoom]);
  const endZoneResize = useCallback((e: React.PointerEvent) => {
    if (zoneResizeRef.current.id) {
      (e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId);
      zoneResizeRef.current.id = null;
    }
  }, []);

  // === Auto-fit canvas size ===
  const baseCanvasW = activeZone?.canvas_width_px ?? 1200;
  const baseCanvasH = activeZone?.canvas_height_px ?? 800;
  const [autoCanvasW, setAutoCanvasW] = useState(baseCanvasW);
  const [autoCanvasH, setAutoCanvasH] = useState(baseCanvasH);
  // Reset to zone's persisted size when active zone changes
  useEffect(() => {
    setAutoCanvasW(baseCanvasW);
    setAutoCanvasH(baseCanvasH);
  }, [activeZone?.id, baseCanvasW, baseCanvasH]);
  const canvasW = isAllZones
    ? Math.max(CANVAS_MIN_W, Math.ceil(allView.boundingBox.width + ALL_VIEW_PAD * 2))
    : autoCanvasW;
  const canvasH = isAllZones
    ? Math.max(CANVAS_MIN_H, Math.ceil(allView.boundingBox.height + ALL_VIEW_PAD * 2))
    : autoCanvasH;
  const [extendedNotice, setExtendedNotice] = useState<string | null>(null);
  const noticeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const flashNotice = useCallback((w: number, h: number) => {
    setExtendedNotice(`Plan agrandi à ${Math.round(w)} × ${Math.round(h)} px`);
    if (noticeTimerRef.current) clearTimeout(noticeTimerRef.current);
    noticeTimerRef.current = setTimeout(() => setExtendedNotice(null), 1400);
  }, []);

  // Track container size for minimap + pan clamping
  const [containerSize, setContainerSize] = useState<{ w: number; h: number }>({ w: 0, h: 0 });

  // Bind pan so at least 25% of the canvas remains visible in the container.
  const clampPan = useCallback((p: { x: number; y: number }, z: number) => {
    const cw = canvasW * z;
    const ch = canvasH * z;
    const W = containerSize.w || (containerRef.current?.clientWidth ?? 0);
    const H = containerSize.h || (containerRef.current?.clientHeight ?? 0);
    if (!W || !H) return p;
    const minVisX = Math.min(cw, W) * 0.25;
    const minVisY = Math.min(ch, H) * 0.25;
    const x = Math.max(minVisX - cw, Math.min(W - minVisX, p.x));
    const y = Math.max(minVisY - ch, Math.min(H - minVisY, p.y));
    return { x, y };
  }, [canvasW, canvasH, containerSize]);

  // Compute desired canvas bounds from positions; grow immediately, shrink with debounce.
  // Drag-time growth is handled inline in onDrag/onDragGroup for instant feedback.
  const desiredCanvas = useMemo(() => {
    if (positions.length === 0) return { w: CANVAS_MIN_W, h: CANVAS_MIN_H };
    let maxR = 0, maxB = 0;
    for (const p of positions) {
      if (p.x + p.width > maxR) maxR = p.x + p.width;
      if (p.y + p.height > maxB) maxB = p.y + p.height;
    }
    return {
      w: Math.max(CANVAS_MIN_W, Math.ceil((maxR + CANVAS_MARGIN) / SNAP) * SNAP),
      h: Math.max(CANVAS_MIN_H, Math.ceil((maxB + CANVAS_MARGIN) / SNAP) * SNAP),
    };
  }, [positions]);

  useEffect(() => {
    if (isAllZones) return; // canvas size is derived from zone bounding box
    // Immediate grow
    if (desiredCanvas.w > autoCanvasW) { setAutoCanvasW(desiredCanvas.w); flashNotice(desiredCanvas.w, autoCanvasH); }
    if (desiredCanvas.h > autoCanvasH) { setAutoCanvasH(desiredCanvas.h); flashNotice(autoCanvasW, desiredCanvas.h); }
    // Debounced shrink (only when comfortably below)
    const t = setTimeout(() => {
      if (isDraggingTables) return;
      if (autoCanvasW - desiredCanvas.w > CANVAS_SHRINK_THRESHOLD) setAutoCanvasW(desiredCanvas.w);
      if (autoCanvasH - desiredCanvas.h > CANVAS_SHRINK_THRESHOLD) setAutoCanvasH(desiredCanvas.h);
    }, 400);
    return () => clearTimeout(t);
  }, [desiredCanvas, autoCanvasW, autoCanvasH, isDraggingTables, flashNotice, isAllZones]);

  // Persist canvas size to the zone (debounced, skip during drag, skip in all-view)
  useEffect(() => {
    if (!activeZone || isDraggingTables || isAllZones) return;
    if (autoCanvasW === activeZone.canvas_width_px && autoCanvasH === activeZone.canvas_height_px) return;
    const t = setTimeout(() => {
      supabase
        .from('floor_plan_zones')
        .update({ canvas_width_px: autoCanvasW, canvas_height_px: autoCanvasH })
        .eq('id', activeZone.id)
        .then(({ error }) => { if (error) console.error('[v2] persist canvas size', error); });
    }, 1500);
    return () => clearTimeout(t);
  }, [autoCanvasW, autoCanvasH, activeZone, isDraggingTables]);

  // Manual "compact" — force canvas to exactly fit current content + margin.
  const handleCompactCanvas = useCallback(() => {
    setAutoCanvasW(desiredCanvas.w);
    setAutoCanvasH(desiredCanvas.h);
    flashNotice(desiredCanvas.w, desiredCanvas.h);
  }, [desiredCanvas, flashNotice]);


  const dayReservationsForDate = useMemo(() => {
    if (!selectedDate) return [] as Reservation[];
    const k = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`;
    return reservations.filter(r => {
      const d = r.date;
      if (!d) return false;
      const kk = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      return kk === k;
    });
  }, [reservations, selectedDate]);

  // === Status per table for service mode ===
  const tableStatusMap = useMemo(() => {
    const map = new Map<string, { status: ReturnType<typeof getTableStatus>['status']; reservation?: Reservation }>();
    if ((mode !== 'service' && !isAllZones) || !selectedTime || !selectedDate) return map;
    const now = new Date();
    const conflictOptions = reservationSettings?.bookingMode === 'time'
      ? {
          bookingMode: 'time' as const,
          reservationDurationMinutes: reservationSettings.reservationDurationMinutes,
          slotEnd: slotEndMap.get(selectedTime),
        }
      : undefined;
    for (const t of tables) {
      const res = getTableStatus(t.id, 'single', dayReservationsForDate, selectedTime, t, now, combinedTables, conflictOptions);
      map.set(t.id, res);
    }
    return map;
  }, [mode, selectedTime, selectedDate, tables, dayReservationsForDate, combinedTables, reservationSettings, slotEndMap]);

  // B1/B2 — per-table day reservations (covers physical + combined assignments)
  const tableDayReservationsMap = useMemo(() => {
    const map = new Map<string, Reservation[]>();
    // Always compute so the search palette can find clients regardless of mode/zone.

    for (const r of dayReservationsForDate) {
      const ids: string[] = [];
      if (r.tableId) ids.push(r.tableId);
      else if (r.combinedTableId) {
        const ct = combinedTables.find(c => c.id === r.combinedTableId);
        if (ct?.tableIds) ids.push(...ct.tableIds);
      }
      for (const id of ids) {
        const arr = map.get(id) ?? [];
        arr.push(r);
        map.set(id, arr);
      }
    }
    for (const arr of map.values()) {
      arr.sort((a, b) => compareServiceTime(a.startTime, b.startTime));
    }
    return map;
  }, [mode, dayReservationsForDate, combinedTables, compareServiceTime]);

  // Étape 7 — occupation par zone (et global) pour les badges d'onglet
  const zoneOccupancy = useMemo(() => {
    type Stat = { occupied: number; total: number };
    const map = new Map<string, Stat>();
    const all: Stat = { occupied: 0, total: 0 };
    const isOccupied = (s?: string) =>
      s === 'arrived' || s === 'reserved' || s === 'late' || s === 'no-show';
    for (const t of tables) {
      if (!t.isActive) continue;
      const key = t.zoneId ?? '__no_zone__';
      const stat = map.get(key) ?? { occupied: 0, total: 0 };
      stat.total += 1;
      all.total += 1;
      const status = tableStatusMap.get(t.id)?.status;
      if (isOccupied(status)) {
        stat.occupied += 1;
        all.occupied += 1;
      }
      map.set(key, stat);
    }
    map.set('__all__', all);
    return map;
  }, [tables, tableStatusMap]);


  // === Cross-highlight (panel ↔ canvas) ===
  const [highlightedReservationId, setHighlightedReservationId] = useState<string | null>(null);
  const highlightedTableIds = useMemo(() => {
    if (!highlightedReservationId) return new Set<string>();
    const r = reservations.find(x => x.id === highlightedReservationId);
    if (!r) return new Set<string>();
    if (r.tableId) return new Set([r.tableId]);
    if (r.combinedTableId) {
      const ct = combinedTables.find(c => c.id === r.combinedTableId);
      return new Set<string>(ct?.tableIds ?? []);
    }
    return new Set<string>();
  }, [highlightedReservationId, reservations, combinedTables]);

  // Surbrillance permanente : toutes les tables physiques liées à une réservation
  // combinée active sur le créneau sélectionné (mode service uniquement).
  const permanentCombinedTableIds = useMemo(() => {
    const set = new Set<string>();
    if (mode !== 'service') return set;
    const seen = new Set<string>();
    for (const entry of tableStatusMap.values()) {
      const r = entry.reservation;
      if (!r?.combinedTableId) continue;
      if (seen.has(r.combinedTableId)) continue;
      seen.add(r.combinedTableId);
      const ct = combinedTables.find(c => c.id === r.combinedTableId);
      if (!ct?.tableIds) continue;
      for (const id of ct.tableIds) set.add(id);
    }
    return set;
  }, [mode, tableStatusMap, combinedTables]);

  // === Drag-and-drop from reservation panel: compute valid drop targets per table ===
  const { dragState, endDrag } = useFloorPlanDrag();
  // (hover state is now managed inside V2TableDroppable via react-dnd's isOver)

  // C1 — Drag-to-swap dialog state
  const [swapDialog, setSwapDialog] = useState<{
    dragged: Reservation;
    target: Reservation;
    targetTableId: string;
  } | null>(null);

  // Force-drop dialog state (soft business blockers: capacity, children, stroller, …)
  const [forceDialog, setForceDialog] = useState<{
    draggedId: string;
    targetTableId: string;
    blockers: string[];
    tableName: string;
  } | null>(null);

  // Lookup by id so the floating drag preview and drop helpers can resolve quickly.
  const reservationsById = useMemo(() => {
    const m = new Map<string, Reservation>();
    for (const r of reservations) m.set(r.id, r);
    return m;
  }, [reservations]);

  // Returns: { ok: true } | { ok: 'swap', other } | { ok: false, reason }
  type DropEval =
    | { ok: true }
    | { ok: 'swap'; other: Reservation }
    | { ok: false; reason: 'inactive' | 'same' | 'locked-target' | 'locked-other' | 'capacity-conflict' };

  const dropEvalMap = useMemo(() => {
    const map = new Map<string, DropEval>();
    if (!dragState.isDragging || !dragState.draggedReservation || !selectedDate) return map;
    const dragged = dragState.draggedReservation;
    if (dragged.isLocked) return map; // locked drag — nothing valid
    const dragDur = dragged.durationMinutes ?? (reservationSettings?.reservationDurationMinutes ?? 120);
    const isTime = reservationSettings?.bookingMode === 'time';
    const dragStart = serviceSortMinutes(dragged.startTime.substring(0, 5), timeSlots);
    const dragEnd = dragStart + dragDur;
    const dayK = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`;
    const dayRes = reservations.filter(r => {
      if (!r.date) return false;
      const d = r.date instanceof Date ? r.date : new Date(r.date);
      const k = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      return k === dayK && r.id !== dragged.id && r.status !== 'annulé';
    });

    for (const t of tables) {
      if (!t.isActive) { map.set(t.id, { ok: false, reason: 'inactive' }); continue; }
      if (t.id === dragged.tableId) { map.set(t.id, { ok: false, reason: 'same' }); continue; }

      // Find ALL conflicting reservations on this physical table
      const conflicts: Reservation[] = [];
      for (const other of dayRes) {
        let phys: string[] = [];
        if (other.tableId) phys = [other.tableId];
        else if (other.combinedTableId) {
          phys = combinedTables.find(c => c.id === other.combinedTableId)?.tableIds ?? [];
        }
        if (!phys.includes(t.id)) continue;
        const overlap = !isTime
          ? other.startTime.substring(0, 5) === dragged.startTime.substring(0, 5)
          : (() => {
              const oDur = other.durationMinutes ?? (reservationSettings?.reservationDurationMinutes ?? 120);
              const oStart = serviceSortMinutes(other.startTime.substring(0, 5), timeSlots);
              const oEnd = oStart + oDur;
              return dragStart < oEnd && oStart < dragEnd;
            })();
        if (overlap) conflicts.push(other);
      }

      if (conflicts.length === 0) { map.set(t.id, { ok: true }); continue; }

      // C1 swap: exactly one conflict, single-table assignment, dragged was assigned, both not locked
      if (
        conflicts.length === 1 &&
        dragged.tableId &&
        conflicts[0].tableId === t.id &&
        !conflicts[0].combinedTableId &&
        !conflicts[0].isLocked
      ) {
        map.set(t.id, { ok: 'swap', other: conflicts[0] });
      } else if (conflicts.some(c => c.isLocked)) {
        map.set(t.id, { ok: false, reason: 'locked-other' });
      } else {
        map.set(t.id, { ok: false, reason: 'capacity-conflict' });
      }
    }
    return map;
  }, [dragState.isDragging, dragState.draggedReservation, tables, combinedTables, reservations, selectedDate, reservationSettings, timeSlots]);

  // Derived map (boolean OR 'swap') used by V2Table for drop-state styling
  const dropValidityMap = useMemo(() => {
    const m = new Map<string, boolean | 'swap'>();
    for (const [k, v] of dropEvalMap) {
      m.set(k, v.ok === true ? true : v.ok === 'swap' ? 'swap' : false);
    }
    return m;
  }, [dropEvalMap]);

  // === Click on table in service mode ===
  const handleCanvasClick = (e: React.MouseEvent) => {
    if (mode !== 'service' && !isAllZones) return;
    const tableEl = (e.target as HTMLElement).closest<HTMLElement>('[data-v2-table-id]');
    if (!tableEl) return;
    const tableId = tableEl.getAttribute('data-v2-table-id');
    if (!tableId) return;
    const entry = tableStatusMap.get(tableId);
    if (entry?.status === 'inactive') return;
    if (entry?.reservation && entry.status !== 'cancelled') {
      setHighlightedReservationId(entry.reservation.id);
      // Alt/Option-click → straight to edit (power-user shortcut).
      if (e.altKey && onEditReservation) {
        onEditReservation(entry.reservation);
        return;
      }
      if (onPreviewReservation) {
        onPreviewReservation(entry.reservation);
      } else if (onEditReservation) {
        onEditReservation(entry.reservation);
      }
      return;
    }
    if (entry?.status === 'cancelled') return;
    if (selectedTime && selectedDate && onCreateAtTable) {
      onCreateAtTable(tableId, selectedTime, selectedDate);
    }
  };

  // === react-dnd helpers for V2TableDroppable ===

  /** Hard blockers: physical/locked. These cannot be forced (SQL trigger would reject). */
  const isHardAllowed = useCallback((tableId: string, item: ReservationDragItem) => {
    if (!onTableChange) return false;
    const r = reservationsById.get(item.reservationId);
    if (!r || r.isLocked || r.status === 'annulé') return false;
    const e = dropEvalMap.get(tableId);
    if (!e) return true; // table outside service eval — let drop attempt
    if (e.ok === true || e.ok === 'swap') return true;
    return false; // inactive, same, locked-target, locked-other, capacity-conflict
  }, [onTableChange, reservationsById, dropEvalMap]);

  /** Visual state used by V2Table (ring colors). */
  const resolveDropState = useCallback((
    tableId: string,
    item: ReservationDragItem | null,
    hovered: boolean,
  ): 'valid' | 'invalid' | 'hover-valid' | 'hover-invalid' | 'swap' | 'hover-swap' | null => {
    if (!item) return null;
    const v = dropValidityMap.get(tableId);
    if (v === 'swap') return hovered ? 'hover-swap' : 'swap';
    if (v === true) return hovered ? 'hover-valid' : 'valid';
    return hovered ? 'hover-invalid' : 'invalid';
  }, [dropValidityMap]);

  /** Called by V2TableDroppable on a successful drop (after canDrop passed). */
  const handleReservationDrop = useCallback((reservationId: string, tableId: string) => {
    const draggedRes = reservationsById.get(reservationId);
    if (!draggedRes || !onTableChange) { endDrag(); return; }
    const evalResult = dropEvalMap.get(tableId);
    if (!evalResult || evalResult.ok === false) {
      // Should not reach here because canDrop blocks it, but be defensive.
      const reason = evalResult?.ok === false ? evalResult.reason : undefined;
      toast.error(
        reason === 'locked-other'
          ? "La table cible est verrouillée par une autre réservation."
          : draggedRes.isLocked
            ? "Cette réservation est verrouillée 🔒."
            : "Cette table n'est pas disponible à ce créneau.",
      );
      endDrag();
      return;
    }
    const targetTable = tables.find(t => t.id === tableId);

    // C1 — Swap path: open confirm dialog
    if (evalResult.ok === 'swap') {
      setSwapDialog({ dragged: draggedRes, target: evalResult.other, targetTableId: tableId });
      endDrag();
      return;
    }

    // Soft business rules — open ForceDropDialog if violated
    if (targetTable) {
      const { blockers, warnings } = validateReservationDrop(draggedRes, targetTable);
      if (blockers.length > 0) {
        setForceDialog({
          draggedId: draggedRes.id,
          targetTableId: tableId,
          blockers,
          tableName: targetTable.name,
        });
        endDrag();
        return;
      }
      if (warnings.length > 0) {
        for (const w of warnings) toast.warning(w);
      }
    }

    onTableChange(draggedRes.id, tableId, null);
    toast.success(`Réservation déplacée sur ${targetTable?.name ?? 'la table'}.`);
    endDrag();
  }, [reservationsById, onTableChange, dropEvalMap, tables, endDrag]);

  /** Force flow — user confirmed ignoring soft blockers. */
  const confirmForceDrop = useCallback(() => {
    if (!forceDialog || !onTableChange) { setForceDialog(null); return; }
    const targetTable = tables.find(t => t.id === forceDialog.targetTableId);
    onTableChange(forceDialog.draggedId, forceDialog.targetTableId, null);
    toast.success(`Assignation forcée sur ${targetTable?.name ?? 'la table'}.`);
    setForceDialog(null);
  }, [forceDialog, onTableChange, tables]);

  const confirmSwap = useCallback(async () => {
    if (!swapDialog) return;
    const { dragged, target } = swapDialog;
    try {
      if (onSwapTables) {
        await onSwapTables(dragged.id, target.id);
      } else if (onTableChange) {
        // Fallback (legacy) — known to fail when PHYSICAL_TABLE_OVERLAP trigger is enforced
        onTableChange(dragged.id, target.tableId ?? null, target.combinedTableId ?? null);
        onTableChange(target.id, dragged.tableId ?? null, dragged.combinedTableId ?? null);
      }
      toast.success("Tables échangées.");
    } catch (e: any) {
      toast.error(e?.message ?? "Impossible d'échanger les tables.");
    } finally {
      setSwapDialog(null);
    }
  }, [swapDialog, onSwapTables, onTableChange]);


  // Build moveable targets from selected DOM elements
  const targets = useMemo(() => {
    if (!canvasRef.current || selectedIds.length === 0) return [] as HTMLElement[];
    return selectedIds
      .map(id => canvasRef.current!.querySelector<HTMLElement>(`[data-v2-table-id="${id}"]`))
      .filter(Boolean) as HTMLElement[];
  }, [selectedIds, positions]);

  // Drop selected ids that no longer exist in current positions
  useEffect(() => {
    if (selectedIds.length === 0) return;
    const valid = new Set(positions.map(p => p.tableId));
    const filtered = selectedIds.filter(id => valid.has(id));
    if (filtered.length !== selectedIds.length) setSelectedIds(filtered);
  }, [positions, selectedIds]);

  // Refresh moveable when targets / positions change
  useEffect(() => {
    moveableRef.current?.updateRect();
  }, [targets, positions]);

  // Overlap detection
  const overlappingIds = useMemo(() => {
    const ids = new Set<string>();
    for (let i = 0; i < positions.length; i++) {
      for (let j = i + 1; j < positions.length; j++) {
        const a = positions[i], b = positions[j];
        const noOverlap = a.x >= b.x + b.width || a.x + a.width <= b.x ||
                          a.y >= b.y + b.height || a.y + a.height <= b.y;
        if (!noOverlap) { ids.add(a.tableId); ids.add(b.tableId); }
      }
    }
    return ids;
  }, [positions]);

  // Refs for keyboard handler (avoid stale closures, defined after handlers below)
  const selectedIdsRef = useRef<string[]>([]);
  const handleApplyPresetRef = useRef<(size: number) => void>(() => {});
  const handleScaleSelectionRef = useRef<(factor: number) => void>(() => {});
  useEffect(() => { selectedIdsRef.current = selectedIds; }, [selectedIds]);

  // Keyboard shortcuts
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') return;
      const meta = e.metaKey || e.ctrlKey;
      if (meta && e.key.toLowerCase() === 'z' && !e.shiftKey) { e.preventDefault(); undo(); }
      else if (meta && (e.key.toLowerCase() === 'z' && e.shiftKey || e.key.toLowerCase() === 'y')) { e.preventDefault(); redo(); }
      else if (meta && e.key.toLowerCase() === 's') { e.preventDefault(); save(); }
      else if (e.key === 'Escape') setSelectedIds([]);
      else if (!meta && selectedIdsRef.current.length > 0) {
        if (e.key === '[') { e.preventDefault(); handleScaleSelectionRef.current(0.9); }
        else if (e.key === ']') { e.preventDefault(); handleScaleSelectionRef.current(1.1); }
        else if (e.key === '1') { e.preventDefault(); handleApplyPresetRef.current(64); }
        else if (e.key === '2') { e.preventDefault(); handleApplyPresetRef.current(80); }
        else if (e.key === '3') { e.preventDefault(); handleApplyPresetRef.current(104); }
        else if (e.key === '4') { e.preventDefault(); handleApplyPresetRef.current(128); }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [undo, redo, save]);

  const snap = (v: number) => Math.round(v / SNAP) * SNAP;

  // Capture initial positions of all selected (or single dragged) tables.
  // We read from the actual DOM (`style.left/top`) so the drag math works whether
  // the table is rendered zone-local OR in the global layer (Étape 12.1.b).
  const captureDragStarts = useCallback((targetEls: HTMLElement[], clientX: number, clientY: number) => {
    dragRef.current = { startClientX: clientX, startClientY: clientY, starts: new Map() };
    targetEls.forEach(el => {
      const id = el.getAttribute('data-v2-table-id');
      const pos = positions.find(p => p.tableId === id);
      if (!id || !pos) return;
      const domX = parseFloat(el.style.left) || pos.x;
      const domY = parseFloat(el.style.top) || pos.y;
      // Parse current visual rotation from inline transform `rotate(Xdeg)` (falls back to pos.rotation).
      const tr = el.style.transform || '';
      const rotMatch = tr.match(/rotate\((-?\d+(?:\.\d+)?)deg\)/);
      const domRot = rotMatch ? parseFloat(rotMatch[1]) : pos.rotation;
      dragRef.current.starts.set(id, { x: domX, y: domY, w: pos.width, h: pos.height, rot: domRot });
    });
  }, [positions]);

  // Étape 9 — édition de tables aussi en vue Toutes.
  // Étape 10 — en sous-mode "Disposer les zones", on désactive l'édition tables.
  const canEditTables = mode === 'edit' && !(isAllZones && arrangeZones);

  // Étape 9 — rotation guard : on n'autorise pas le drag/rotate d'une table si sa zone parente
  // est tournée (≠ 0°). On propose un bouton « Remettre à plat » dans le toast.
  const guardTableIdsForEdit = useCallback((ids: string[]): boolean => {
    if (selectedZoneId !== '__all__') return true;
    for (const id of ids) {
      const p = positions.find(pp => pp.tableId === id);
      if (!p?.zoneId) continue;
      const z = allView.zones.find(x => x.id === p.zoneId);
      if (z && z.rotation !== 0) {
        toast.error(`Remettre la zone "${z.name}" à 0° pour éditer ses tables`, {
          action: {
            label: 'Remettre à plat',
            onClick: () => { allView.snapshot(); allView.updateZoneRotation(z.id, 0); },
          },
        });
        return false;
      }
    }
    return true;
  }, [selectedZoneId, positions, allView]);


  const onDragStart = useCallback((e: any) => {
    const id = e.target.getAttribute('data-v2-table-id');
    if (id && !guardTableIdsForEdit([id])) { e.stop?.(); return false; }
    setIsDraggingTables(true);
    snapshot();
    const inp = e.inputEvent as MouseEvent;
    captureDragStarts([e.target], inp?.clientX ?? e.clientX ?? 0, inp?.clientY ?? e.clientY ?? 0);
  }, [snapshot, captureDragStarts, guardTableIdsForEdit]);
  const onRotateStart = useCallback((e: any) => {
    const id = e?.target?.getAttribute?.('data-v2-table-id');
    if (id && !guardTableIdsForEdit([id])) { e.stop?.(); return false; }
    snapshot();
  }, [snapshot, guardTableIdsForEdit]);

  const onDrag = useCallback((e: any) => {
    const id = e.target.getAttribute('data-v2-table-id');
    const start = id && dragRef.current.starts.get(id);
    if (!start) return;
    const inp = e.inputEvent as MouseEvent | undefined;
    const cx = inp?.clientX ?? e.clientX;
    const cy = inp?.clientY ?? e.clientY;
    const z = zoom || 1;
    const dx = (cx - dragRef.current.startClientX) / z;
    const dy = (cy - dragRef.current.startClientY) / z;
    const nx = Math.max(0, snap(start.x + dx));
    const ny = Math.max(0, snap(start.y + dy));
    // Grow canvas if needed (no right/bottom wall)
    if (nx + start.w > canvasW - CANVAS_MARGIN / 2) {
      const next = Math.max(canvasW, snap(nx + start.w + CANVAS_MARGIN));
      if (next > canvasW) { setAutoCanvasW(next); flashNotice(next, canvasH); }
    }
    if (ny + start.h > canvasH - CANVAS_MARGIN / 2) {
      const next = Math.max(canvasH, snap(ny + start.h + CANVAS_MARGIN));
      if (next > canvasH) { setAutoCanvasH(next); flashNotice(canvasW, next); }
    }
    e.target.style.left = `${nx}px`;
    e.target.style.top = `${ny}px`;
    // Preserve current visual rotation: use ongoing rotate (dataset.tr) > starting rotation (captured).
    const visRot = e.target.dataset.tr ?? String(start.rot ?? 0);
    e.target.style.transform = visRot && visRot !== '0' ? `rotate(${visRot}deg)` : '';
    e.target.dataset.tx = String(nx);
    e.target.dataset.ty = String(ny);
  }, [canvasW, canvasH, zoom, positions]);

  const onDragEnd = useCallback((e: any) => {
    setIsDraggingTables(false);
    const id = e.target.getAttribute('data-v2-table-id');
    const pos = positions.find(p => p.tableId === id);
    if (!pos) return;
    const nx = Number(e.target.dataset.tx ?? pos.x);
    const ny = Number(e.target.dataset.ty ?? pos.y);
    delete e.target.dataset.tx;
    delete e.target.dataset.ty;
    // Étape 12.1.b — in all-zones table edit, (nx, ny) are global coords (offX/offY included).
    // Convert back to the table's zone-local frame before persisting. With rotation 0 this is identity.
    const isAllZonesTableEdit = isAllZones && mode === 'edit' && !arrangeZones;
    if (isAllZonesTableEdit && pos.zoneId) {
      const z = allView.zones.find(zz => zz.id === pos.zoneId);
      if (z) {
        const offX = ALL_VIEW_PAD - allView.boundingBox.minX;
        const offY = ALL_VIEW_PAD - allView.boundingBox.minY;
        const local = localFromGlobal(z as AllViewZoneLite, nx - offX, ny - offY);
        const lx = Math.round(local.x);
        const ly = Math.round(local.y);
        if (lx === pos.x && ly === pos.y) return;
        updatePosition({ ...pos, x: lx, y: ly });
        return;
      }
    }
    if (nx === pos.x && ny === pos.y) return;
    updatePosition({ ...pos, x: nx, y: ny });
  }, [positions, updatePosition, isAllZones, mode, arrangeZones, allView]);

  const onRotate = useCallback((e: any) => {
    const id = e.target.getAttribute('data-v2-table-id');
    const pos = positions.find(p => p.tableId === id);
    if (!pos) return;
    e.target.style.transform = `rotate(${e.rotate}deg)`;
    e.target.dataset.tr = String(e.rotate);
  }, [positions]);

  const onRotateEnd = useCallback((e: any) => {
    const id = e.target.getAttribute('data-v2-table-id');
    const pos = positions.find(p => p.tableId === id);
    if (!pos) return;
    const r = Number(e.target.dataset.tr ?? pos.rotation);
    // Étape 12.1.b — in all-zones table edit, `r` is the global rotation; convert to zone-local.
    const isAllZonesTableEdit = isAllZones && mode === 'edit' && !arrangeZones;
    if (isAllZonesTableEdit && pos.zoneId) {
      const z = allView.zones.find(zz => zz.id === pos.zoneId);
      if (z) {
        const localR = localRotFromGlobal(r, z.rotation);
        updatePosition({ ...pos, rotation: localR });
        return;
      }
    }
    updatePosition({ ...pos, rotation: r });
  }, [positions, updatePosition, isAllZones, mode, arrangeZones, allView]);

  // Toolbar actions
  const selectedPositions = positions.filter(p => selectedIds.includes(p.tableId));

  const handleRotate15 = () => {
    if (selectedIds.length === 0) return;
    snapshot();
    updateMany(prev => prev.map(p =>
      selectedIds.includes(p.tableId) ? { ...p, rotation: (p.rotation + 15) % 360 } : p
    ));
  };

  /** Apply a preset side-length to all selected tables, shape-aware, recentered. */
  const handleApplyPreset = useCallback((size: number) => {
    if (selectedIds.length === 0) return;
    snapshot();
    updateMany(prev => prev.map(p => {
      if (!selectedIds.includes(p.tableId)) return p;
      const isUniform = p.shape === 'square' || p.shape === 'round';
      const ratio = p.height === 0 ? 1 : p.width / p.height;
      const newW = isUniform ? size : Math.max(MIN_SIZE, Math.round(size * Math.max(1, ratio) / SNAP) * SNAP);
      const newH = isUniform ? size : size;
      const cx = p.x + p.width / 2;
      const cy = p.y + p.height / 2;
      const nx = Math.max(0, Math.min(canvasW - newW, Math.round((cx - newW / 2) / SNAP) * SNAP));
      const ny = Math.max(0, Math.min(canvasH - newH, Math.round((cy - newH / 2) / SNAP) * SNAP));
      return { ...p, width: newW, height: newH, x: nx, y: ny };
    }));
  }, [selectedIds, snapshot, updateMany, canvasW, canvasH]);

  /** Scale all selected tables by factor around their own center. */
  const handleScaleSelection = useCallback((factor: number) => {
    if (selectedIds.length === 0) return;
    snapshot();
    updateMany(prev => prev.map(p => {
      if (!selectedIds.includes(p.tableId)) return p;
      const newW = Math.max(MIN_SIZE, Math.round((p.width * factor) / SNAP) * SNAP);
      const newH = Math.max(MIN_SIZE, Math.round((p.height * factor) / SNAP) * SNAP);
      const cx = p.x + p.width / 2;
      const cy = p.y + p.height / 2;
      const nx = Math.max(0, Math.min(canvasW - newW, Math.round((cx - newW / 2) / SNAP) * SNAP));
      const ny = Math.max(0, Math.min(canvasH - newH, Math.round((cy - newH / 2) / SNAP) * SNAP));
      return { ...p, width: newW, height: newH, x: nx, y: ny };
    }));
  }, [selectedIds, snapshot, updateMany, canvasW, canvasH]);

  // Sync refs for keyboard handler
  useEffect(() => { handleApplyPresetRef.current = handleApplyPreset; }, [handleApplyPreset]);
  useEffect(() => { handleScaleSelectionRef.current = handleScaleSelection; }, [handleScaleSelection]);

  const handleZoomIn = () => setZoom(z => Math.min(2, z + 0.1));
  const handleZoomOut = () => setZoom(z => Math.max(0.3, z - 0.1));
  const handleZoomFit = useCallback((forService = false) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;
    const cap = forService ? 1.5 : 2;
    const margin = forService ? 0.92 : 0.98;

    // Fit on the actual table content bbox when available, else fall back to full canvas.
    let fitW = canvasW;
    let fitH = canvasH;
    let originX = 0;
    let originY = 0;
    if (!isAllZones && positions.length > 0) {
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      for (const p of positions) {
        if (p.x < minX) minX = p.x;
        if (p.y < minY) minY = p.y;
        if (p.x + p.width > maxX) maxX = p.x + p.width;
        if (p.y + p.height > maxY) maxY = p.y + p.height;
      }
      const pad = CANVAS_MARGIN / 2;
      originX = Math.max(0, minX - pad);
      originY = Math.max(0, minY - pad);
      fitW = Math.max(1, (maxX - minX) + pad * 2);
      fitH = Math.max(1, (maxY - minY) + pad * 2);
    }

    const z = Math.min(rect.width / fitW, rect.height / fitH, cap) * margin;
    setZoom(z);
    setPan({
      x: (rect.width - fitW * z) / 2 - originX * z,
      y: (rect.height - fitH * z) / 2 - originY * z,
    });
  }, [canvasW, canvasH, positions, isAllZones]);

  // Auto-fit on mount, on zone change, on mode change, on positions load, and on container resize.
  // Also keeps containerSize in sync for the minimap and clampPan.
  useEffect(() => {
    if (!containerRef.current) return;
    const el = containerRef.current;
    const sync = () => {
      const r = el.getBoundingClientRect();
      setContainerSize({ w: r.width, h: r.height });
      // Defer to next frame so layout (reservation panel, container resize) has settled.
      requestAnimationFrame(() => handleZoomFit(mode === 'service'));
    };
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    return () => ro.disconnect();
  }, [editingZoneId, handleZoomFit, mode, canvasW, canvasH, positions.length]);

  // === Keyboard shortcuts for the "Toutes" view ===
  // R = cycle rotation of selected zone (edit mode), Space = temporary pan,
  // Cmd/Ctrl+Z / Cmd/Ctrl+Shift+Z = undo/redo (édition zones only).
  useEffect(() => {
    if (!isAllZones) return;
    const isTypingTarget = (t: EventTarget | null) => {
      const el = t as HTMLElement | null;
      if (!el || !el.tagName) return false;
      const tag = el.tagName.toLowerCase();
      return tag === 'input' || tag === 'textarea' || tag === 'select' || el.isContentEditable;
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (isTypingTarget(e.target)) return;
      // Space → pan mode
      if (e.code === 'Space' && !e.repeat) {
        e.preventDefault();
        setSpacePanActive(true);
        return;
      }
      // Undo / Redo (édition zones only)
      if (isAllZonesEdit && (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        if (e.shiftKey) allView.redo(); else allView.undo();
        return;
      }
      // Rotate selected zone (édition zones only)
      if (isAllZonesEdit && (e.key === 'r' || e.key === 'R') && !e.metaKey && !e.ctrlKey && !e.altKey) {
        if (!selectedAllZoneId) return;
        const z = allView.zones.find(x => x.id === selectedAllZoneId);
        if (!z) return;
        const next = ((z.rotation + 90) % 360) as 0 | 90 | 180 | 270;
        e.preventDefault();
        allView.snapshot();
        allView.updateZoneRotation(z.id, next);
      }
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.code === 'Space') setSpacePanActive(false);
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('keyup', onKeyUp);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('keyup', onKeyUp);
    };
  }, [isAllZones, isAllZonesEdit, selectedAllZoneId, allView]);




  // Pointer down on a table → drive selection + Moveable.dragStart manually,
  // bypassing Selecto so the drag begins immediately on the table.
  const onCanvasPointerDown = (e: React.PointerEvent) => {
    if (mode !== 'edit') return;
    if (e.button !== 0) return;
    const target = e.target as HTMLElement;
    // Let Moveable handles (resize/rotate) and overlay area receive the gesture.
    if (target.closest('.moveable-control, .moveable-line, .moveable-direction, .moveable-rotation')) return;
    const tableEl = target.closest<HTMLElement>('[data-v2-table-id]');
    if (!tableEl) return;
    const id = tableEl.getAttribute('data-v2-table-id');
    if (!id) return;

    const moveable = moveableRef.current;
    const native = e.nativeEvent;

    if (e.shiftKey) {
      setSelectedIds(prev =>
        prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
      );
      e.stopPropagation();
      return;
    }

    if (selectedIds.includes(id)) {
      e.stopPropagation();
      moveable?.dragStart(native, tableEl);
      return;
    }

    e.stopPropagation();
    setSelectedIds([id]);
    moveable?.waitToChangeTarget().then(() => {
      moveableRef.current?.dragStart(native, tableEl);
    });
  };

  // Pan: drag background only (disabled in Service mode)
  // In edit mode, only with Alt or middle-click — plain drag is reserved for lasso selection.
  const onContainerMouseDown = (e: React.MouseEvent) => {
    if (mode === 'service') return;
    const target = e.target as HTMLElement;
    if (!target.dataset.panBg) return;
    const isPanGesture = e.button === 1 || e.altKey;
    if (!isPanGesture) return;
    panStateRef.current = {
      active: true,
      startX: e.clientX,
      startY: e.clientY,
      startPanX: pan.x,
      startPanY: pan.y,
    };
    (e.currentTarget as HTMLElement).style.cursor = 'grabbing';
  };
  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!panStateRef.current.active) return;
      const dx = e.clientX - panStateRef.current.startX;
      const dy = e.clientY - panStateRef.current.startY;
      setPan(clampPan({ x: panStateRef.current.startPanX + dx, y: panStateRef.current.startPanY + dy }, zoom));
    };
    const onUp = () => {
      if (panStateRef.current.active) {
        panStateRef.current.active = false;
        if (containerRef.current) containerRef.current.style.cursor = 'grab';
      }
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [clampPan, zoom]);

  // Wheel: Cmd/Ctrl + wheel = zoom on pointer; plain wheel = pan (disabled in Service)
  // En vue « Toutes » + Édition : zoom/pan verrouillés (sauf Ctrl/Cmd) pour éviter
  // les zooms involontaires pendant qu'on déplace une table.
  const isAllEdit = mode === 'edit' && selectedZoneId === null;
  const onWheel = (e: React.WheelEvent) => {
    if (mode === 'service') return;
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const px = e.clientX - rect.left;
      const py = e.clientY - rect.top;
      const delta = -e.deltaY * 0.0015;
      const newZoom = Math.max(0.2, Math.min(2.5, zoom * (1 + delta)));
      // keep point under cursor stable
      const k = newZoom / zoom;
      setPan(clampPan({ x: px - (px - pan.x) * k, y: py - (py - pan.y) * k }, newZoom));
      setZoom(newZoom);
    } else {
      if (isAllEdit) return; // zoom/pan verrouillés en vue Toutes + Édition
      setPan(p => clampPan({ x: p.x - e.deltaX, y: p.y - e.deltaY }, zoom));
    }
  };

  // === Touch pinch-zoom + 1-finger pan on background (iPad / mobile) ===
  // Single-finger pan only when the touch starts on the canvas background
  // (data-panBg), so dragging a reservation card via TouchBackend still works.
  // Two-finger pinch always zooms regardless of target.
  const touchRef = useRef<{
    mode: 'idle' | 'pan' | 'pinch';
    startPanX: number;
    startPanY: number;
    startX: number;
    startY: number;
    startDist: number;
    startZoom: number;
    midX: number;
    midY: number;
  }>({ mode: 'idle', startPanX: 0, startPanY: 0, startX: 0, startY: 0, startDist: 0, startZoom: 1, midX: 0, midY: 0 });

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const isPanBgTarget = (t: EventTarget | null) => {
      const node = t as HTMLElement | null;
      if (!node) return false;
      // Pan only when touching empty canvas/viewport background, not a table/fixture/control.
      return !node.closest('[data-fp-table], [data-fp-fixture], button, a, input, [role="button"], [draggable="true"]');
    };
    const onTouchStart = (e: TouchEvent) => {
      const rect = el.getBoundingClientRect();
      // Mode service : plan totalement statique au toucher (parité desktop).
      // Pas de pan 1 doigt, pas de pinch-zoom. Les taps restent fonctionnels.
      if (mode === 'service') return;
      // Lock pinch-zoom and bg pan in « Toutes + Édition » to avoid accidental zoom while dragging tables
      if (mode === 'edit' && selectedZoneId === null) return;
      if (e.touches.length === 2) {
        e.preventDefault();
        const t1 = e.touches[0];
        const t2 = e.touches[1];
        const dx = t1.clientX - t2.clientX;
        const dy = t1.clientY - t2.clientY;
        touchRef.current = {
          mode: 'pinch',
          startPanX: pan.x,
          startPanY: pan.y,
          startX: 0,
          startY: 0,
          startDist: Math.hypot(dx, dy) || 1,
          startZoom: zoom,
          midX: (t1.clientX + t2.clientX) / 2 - rect.left,
          midY: (t1.clientY + t2.clientY) / 2 - rect.top,
        };
      } else if (e.touches.length === 1 && isPanBgTarget(e.target)) {
        const t1 = e.touches[0];
        touchRef.current = {
          ...touchRef.current,
          mode: 'pan',
          startPanX: pan.x,
          startPanY: pan.y,
          startX: t1.clientX,
          startY: t1.clientY,
        };
      }
    };
    const onTouchMove = (e: TouchEvent) => {
      // Mode service : aucun déplacement/zoom tactile.
      if (mode === 'service') return;
      const st = touchRef.current;

      if (st.mode === 'pinch' && e.touches.length === 2) {
        e.preventDefault();
        const t1 = e.touches[0];
        const t2 = e.touches[1];
        const dist = Math.hypot(t1.clientX - t2.clientX, t1.clientY - t2.clientY) || 1;
        const ratio = dist / st.startDist;
        const newZoom = Math.max(0.2, Math.min(2.5, st.startZoom * ratio));
        const k = newZoom / st.startZoom;
        const newPan = clampPan(
          { x: st.midX - (st.midX - st.startPanX) * k, y: st.midY - (st.midY - st.startPanY) * k },
          newZoom,
        );
        setZoom(newZoom);
        setPan(newPan);
      } else if (st.mode === 'pan' && e.touches.length === 1) {
        e.preventDefault();
        const t1 = e.touches[0];
        const dx = t1.clientX - st.startX;
        const dy = t1.clientY - st.startY;
        setPan(clampPan({ x: st.startPanX + dx, y: st.startPanY + dy }, zoom));
      }
    };
    const onTouchEnd = (e: TouchEvent) => {
      if (e.touches.length === 0) {
        touchRef.current.mode = 'idle';
      } else if (e.touches.length === 1 && touchRef.current.mode === 'pinch') {
        // Lifting one finger after pinch — stop gesture, do not slide into pan
        touchRef.current.mode = 'idle';
      }
    };
    el.addEventListener('touchstart', onTouchStart, { passive: false });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd);
    el.addEventListener('touchcancel', onTouchEnd);
    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
      el.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [pan.x, pan.y, zoom, clampPan, mode, selectedZoneId]);

  if (loading || zones.length === 0) {
    return <div className="p-8 text-center text-muted-foreground">Chargement du plan…</div>;
  }

  const isEditMode = mode === 'edit' && !isAllZones;
  const isServiceMode = isAllZones ? (mode === 'service') : (mode === 'service');
  const showAllEdit = isAllZones && mode === 'edit';
  // Étape 10 — zone chrome visible uniquement en sous-mode "Disposer les zones"
  const showZoneChrome = showAllEdit && arrangeZones;

  return (
    <TooltipProvider delayDuration={200}>
    <div className={cn("flex flex-col h-full", fullscreenEdit && "fixed inset-0 z-50 bg-background")}>
      {/* Sticky header */}
      <div className="shrink-0 flex flex-wrap items-center justify-between gap-2 px-2 md:px-3 py-2 border-b bg-card min-w-0 overflow-x-hidden">
        <div className="flex items-center gap-2 md:gap-3 flex-wrap min-w-0 max-w-full">
          <div className="-mx-2 px-2 overflow-x-auto max-w-full" style={{ scrollbarWidth: 'none' }}>
          <Tabs value={selectedZoneId ?? ''} onValueChange={v => setSelectedZoneId(v)}>
            <TabsList>
              {zones.map(z => {
                const stat = zoneOccupancy.get(z.id);
                const showStat = isServiceMode && stat && stat.total > 0;
                const pct = showStat ? Math.round((stat.occupied / stat.total) * 100) : 0;
                return (
                  <TabsTrigger key={z.id} value={z.id} className="flex flex-col items-center gap-0.5 py-1.5">
                    <span className="flex items-center">
                      <span className="w-2 h-2 rounded-full mr-1.5" style={{ background: z.color || '#6366f1' }} />
                      {z.name}
                      {showStat && (
                        <span className="ml-1.5 text-[10px] font-mono text-muted-foreground">
                          {stat!.occupied}/{stat!.total}
                        </span>
                      )}
                    </span>
                    {showStat && (
                      <span aria-hidden className="w-full h-0.5 rounded-full bg-muted overflow-hidden">
                        <span className="block h-full bg-primary transition-all" style={{ width: `${pct}%` }} />
                      </span>
                    )}
                  </TabsTrigger>
                );
              })}


            </TabsList>
          </Tabs>
          </div>

          {/* Filtre de surbrillance multi-zones — actif uniquement sur l'onglet « Toutes » en service */}
          {isAllZones && isServiceMode && zones.length >= 2 && (
            <V2ZoneFilterSelect
              zones={zones.map(z => ({ id: z.id, name: z.name, color: z.color }))}
              highlighted={highlightedZoneIds}
              onChange={setHighlightedZoneIds}
              occupancy={zoneOccupancy}
            />
          )}


          {isServiceMode && selectedDate && onDateChange && (
            <div className="flex items-center gap-2 flex-wrap">
              <V2ServiceBar
                date={selectedDate}
                onDateChange={onDateChange}
                timeSlots={slotStrings}
                selectedTime={selectedTime}
                onTimeChange={setSelectedTime}
                slotCovers={slotCoversMap}
              />
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="icon"
                    variant="outline"
                    className="h-8 w-8"
                    onClick={() => setSearchOpen(true)}
                    aria-label="Rechercher une table"
                  >
                    <Search className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Rechercher une table (⌘K / /)</TooltipContent>
              </Tooltip>
            </div>
          )}
          {(isServiceCapable || isAllZones) && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="icon"
                  variant="outline"
                  onClick={() => setMode(m => m === 'service' ? 'edit' : 'service')}
                  className="h-8 w-8"
                  aria-label={mode === 'service' ? 'Passer en édition du plan' : 'Revenir au mode service'}
                >
                  {mode === 'service' ? <Pencil className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </TooltipTrigger>
              <TooltipContent>{mode === 'service' ? 'Passer en édition du plan' : 'Revenir au mode service'}</TooltipContent>
            </Tooltip>
          )}
        </div>


        <div className="flex items-center gap-2 flex-wrap">
          {canEditTables && (
            <V2Toolbar
              singleSelection={selectedPositions.length === 1 ? {
                width: selectedPositions[0].width,
                height: selectedPositions[0].height,
                shape: selectedPositions[0].shape,
                onResize: (w, h) => {
                  snapshot();
                  const id = selectedPositions[0].tableId;
                  updateMany(prev => prev.map(p => p.tableId === id ? { ...p, width: w, height: h } : p));
                },
              } : undefined}
              canUndo={canUndo}
              canRedo={canRedo}
              dirty={dirty}
              saving={saving}
              lastSavedAt={lastSavedAt}
              selectedCount={selectedIds.length}
              zoom={zoom}
              onUndo={undo}
              onRedo={redo}
              onSave={save}
              onRotate={handleRotate15}
              onDuplicate={() => {}}
              onDelete={() => {
                if (selectedIds.length === 0) return;
                snapshot();
                updateMany(prev => prev.filter(p => !selectedIds.includes(p.tableId)));
                setSelectedIds([]);
              }}
              onSetShape={(shape) => {
                if (selectedIds.length === 0) return;
                snapshot();
                updateMany(prev => prev.map(p => selectedIds.includes(p.tableId) ? { ...p, shape } : p));
              }}
              onZoomIn={handleZoomIn}
              onZoomOut={handleZoomOut}
              onZoomFit={handleZoomFit}
              onRecenter={() => handleZoomFit(false)}
              onCompactCanvas={handleCompactCanvas}
              onApplyPreset={handleApplyPreset}
              onScaleSelection={handleScaleSelection}
            />
          )}

          {showAllEdit && (
            <div className="flex items-center gap-1 text-xs">
              <Button
                size="sm"
                variant={arrangeZones ? "default" : "outline"}
                className="h-9 px-2"
                onClick={() => setArrangeZones(v => !v)}
                title="Activer pour déplacer/redimensionner/pivoter les zones"
              >
                {arrangeZones ? "✓ Disposer les zones" : "Disposer les zones"}
              </Button>
              {arrangeZones && (
                <>
                  <Button size="sm" variant="outline" className="h-9 px-2" onClick={allView.undo} disabled={!allView.canUndo}>↶</Button>
                  <Button size="sm" variant="outline" className="h-9 px-2" onClick={allView.redo} disabled={!allView.canRedo}>↷</Button>
                  <Button size="sm" variant="outline" className="h-9 px-2" onClick={() => allView.save()} disabled={!allView.dirty || allView.saving}>
                    {allView.saving ? 'Sauvegarde…' : allView.dirty ? 'Enregistrer' : 'Enregistré'}
                  </Button>
                </>
              )}
            </div>
          )}








        </div>

      </div>


      {/* Body: optional left panel (service mode) + canvas viewport */}
      <div className="flex flex-1 min-h-0">
        {isServiceMode && selectedDate && (
          <>
            {/* Desktop / tablet large: persistent sidebar */}
            <div className="hidden md:flex w-[320px] shrink-0 flex-col">
              <V2ReservationPanel
                selectedDate={selectedDate}
                selectedTime={selectedTime}
                slotEnd={selectedTime ? slotEndMap.get(selectedTime) : undefined}
                reservations={reservations}
                tables={tables}
                combinedTables={combinedTables}
                reservationSettings={reservationSettings}
                highlightedReservationId={highlightedReservationId}
                onHoverReservation={setHighlightedReservationId}
                onSelectReservation={(r) => setHighlightedReservationId(r.id)}
                onEditReservation={onEditReservation}
                onStatusChange={onStatusChange}
                onTableChange={onTableChange}
                onLockChange={onLockChange}
              />
            </div>

            {/* Mobile: floating button → bottom Sheet */}
            <Sheet>
              <SheetTrigger asChild>
                <Button
                  size="sm"
                  variant="default"
                  className="md:hidden fixed right-4 z-40 h-12 px-4 rounded-full shadow-lg gap-2 bottom-[calc(env(safe-area-inset-bottom)+72px)]"
                  aria-label="Ouvrir la liste des réservations"
                >
                  <ListOrdered className="h-5 w-5" />
                  <span className="text-sm font-medium">
                    Résa{dayReservationsForDate.length > 0 ? ` · ${dayReservationsForDate.length}` : ''}
                  </span>
                </Button>
              </SheetTrigger>
              <SheetContent
                side="bottom"
                className="md:hidden p-0 h-[85vh] flex flex-col gap-0"
              >
                <SheetHeader className="px-4 py-3 border-b shrink-0">
                  <SheetTitle className="text-base">Réservations du jour</SheetTitle>
                </SheetHeader>
                <div className="flex-1 min-h-0 flex flex-col overscroll-contain">
                  <V2ReservationPanel
                    selectedDate={selectedDate}
                    selectedTime={selectedTime}
                    slotEnd={selectedTime ? slotEndMap.get(selectedTime) : undefined}
                    reservations={reservations}
                    tables={tables}
                    combinedTables={combinedTables}
                    reservationSettings={reservationSettings}
                    highlightedReservationId={highlightedReservationId}
                    onHoverReservation={setHighlightedReservationId}
                    onSelectReservation={(r) => setHighlightedReservationId(r.id)}
                    onEditReservation={onEditReservation}
                    onStatusChange={onStatusChange}
                    onTableChange={onTableChange}
                    onLockChange={onLockChange}
                  />
                </div>
              </SheetContent>
            </Sheet>
          </>
        )}

      {/* Canvas viewport */}
      <div
        ref={containerRef}
        data-pan-bg
        onPointerDownCapture={onCanvasPointerDown}
        onMouseDown={onContainerMouseDown}
        onWheel={onWheel}
        onClick={handleCanvasClick}
        onDoubleClick={(e) => {
          if (mode === 'service') return;
          if ((e.target as HTMLElement).dataset.panBg) handleZoomFit();
        }}
        className="relative flex-1 min-h-0 overflow-hidden bg-muted/30 fp-v2-viewport"
        data-fp-dragging={isDraggingTables ? 'true' : undefined}
        style={{
          cursor: isServiceMode ? 'default' : (canEditTables ? 'crosshair' : 'default'),
          userSelect: 'none',
          // Service mode: keep plan static on touch (no native pan/zoom, no double-tap zoom).
          // Edit mode: 'none' so our custom pan/pinch handlers fully own gestures.
          touchAction: mode === 'service' ? 'manipulation' : 'none',
        }}
      >
        <div
          ref={canvasRef}
          className={cn("absolute top-0 left-0 bg-background shadow-inner")}
          style={{
            width: canvasW,
            height: canvasH,
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
            transformOrigin: 'top left',
            backgroundImage: 'radial-gradient(circle, hsl(var(--muted-foreground) / 0.18) 1px, transparent 1px)',
            backgroundSize: `${SNAP}px ${SNAP}px`,
          }}
        >
          {(() => {
            // Étape 12.1.b — in Toutes + Édition (hors « Disposer les zones »), tables are rendered
            // in a SINGLE global layer (sibling of zone wrappers), in global coordinates. Removes
            // nested CSS transforms that broke Moveable, and prepares inter-zone drag.
            const isAllZonesTableEdit = showAllEdit && !arrangeZones;
            // Render a single position element using existing droppable / view logic.
            // `displayOverride` lets us swap in global coordinates without mutating the saved `pos`.
            const renderPos = (
              pos: typeof positions[number],
              displayOverride?: { x: number; y: number; rotation: number },
            ) => {
              const table = tables.find(t => t.id === pos.tableId);
              if (!table) return null;
              const zone = zones.find(z => z.id === pos.zoneId);
              const statusEntry = isServiceMode ? tableStatusMap.get(pos.tableId) : undefined;
              const isHl = (isServiceMode && (highlightedTableIds.has(pos.tableId) || permanentCombinedTableIds.has(pos.tableId)))
                || focusedTableId === pos.tableId;
              const canAccept = isServiceMode && !!onTableChange;
              const displayPos = displayOverride
                ? { ...pos, x: displayOverride.x, y: displayOverride.y, rotation: displayOverride.rotation }
                : pos;
              const commonProps = {
                position: displayPos,
                table,
                isSelected: canEditTables && selectedIds.includes(pos.tableId),
                hasOverlap: canEditTables && overlappingIds.has(pos.tableId),
                zoneColor: zone?.color,
                interactionMode: (canEditTables ? 'edit' : (isServiceMode ? 'service' : 'view')) as 'edit' | 'service' | 'view',
                status: statusEntry?.status,
                reservation: statusEntry?.reservation,
                dayReservations: isServiceMode ? tableDayReservationsMap.get(pos.tableId) : undefined,
                isHighlighted: isHl,
                compact: isServiceMode && compact,
              };
              if (canAccept) {
                return (
                  <V2TableDroppable
                    key={pos.tableId}
                    {...commonProps}
                    canAcceptDrops
                    resolveDropState={resolveDropState}
                    onReservationDrop={handleReservationDrop}
                    isHardAllowed={isHardAllowed}
                  />
                );
              }
              return <V2Table key={pos.tableId} {...commonProps} />;
            };

            if (!isAllZones) {
              const singleFixtures = editingZoneId ? (fixturesByZone.get(editingZoneId) ?? []) : [];
              return (
                <>
                  {singleFixtures.map(f => <V2Fixture key={f.id} fixture={f} />)}
                  {positions.map(p => renderPos(p))}
                </>
              );
            }

            // === Vue Toutes : composer chaque zone à sa position globale ===
            const offX = ALL_VIEW_PAD - allView.boundingBox.minX;
            const offY = ALL_VIEW_PAD - allView.boundingBox.minY;
            const posByZone = new Map<string, typeof positions>();
            for (const p of positions) {
              if (!p.zoneId) continue;
              const arr = posByZone.get(p.zoneId) ?? [];
              arr.push(p);
              posByZone.set(p.zoneId, arr);
            }
            const zoneWrappers = allView.zones.map((z) => {
              const zonePositions = posByZone.get(z.id) ?? [];
              const zoneFixtures = fixturesByZone.get(z.id) ?? [];
              const nextRot = ((z.rotation + 90) % 360) as 0 | 90 | 180 | 270;
              // Étape 8 — dim non-highlighted zones (filter actif)
              const isDimmed = highlightedZoneIds.size > 0 && !highlightedZoneIds.has(z.id);
              return (
                <div
                  key={z.id}
                  data-v2-zone-id={z.id}
                  className={cn(
                    "absolute rounded-md transition-opacity",
                    showZoneChrome ? "border-2 ring-1 ring-primary/20" : "border border-transparent",
                    isDimmed && "opacity-30",
                  )}
                  style={{
                    left: offX + z.posX,
                    top: offY + z.posY,
                    width: z.canvasWidth,
                    height: z.canvasHeight,
                    transform: `rotate(${z.rotation}deg)`,
                    transformOrigin: 'top left',
                    background: showZoneChrome
                      ? (z.backgroundColor ?? 'hsl(var(--background))')
                      : (z.backgroundColor
                          ? `color-mix(in srgb, ${z.backgroundColor} 18%, transparent)`
                          : 'transparent'),
                    borderColor: showZoneChrome ? (z.color || 'hsl(var(--border))') : undefined,
                  }}
                >
                  {showZoneChrome ? (
                    /* Zone header / drag handle (sous-mode "Disposer les zones") */
                    <div
                      className={cn(
                        "absolute -top-7 left-0 right-0 flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-t bg-background/95 border border-b-0",
                        "cursor-grab active:cursor-grabbing select-none",
                      )}
                      style={{ borderColor: z.color || 'hsl(var(--border))' }}
                      onPointerDown={!spacePanActive ? (e) => startZoneDrag(e, z.id, z.posX, z.posY) : undefined}
                      onPointerMove={!spacePanActive ? onZoneDragPointerMove : undefined}
                      onPointerUp={!spacePanActive ? endZoneDrag : undefined}
                      onPointerCancel={!spacePanActive ? endZoneDrag : undefined}
                    >
                      <span className="w-2 h-2 rounded-full shrink-0" style={{ background: z.color || '#6366f1' }} />
                      <span className="truncate" style={{ color: z.color || undefined }}>{z.name}</span>
                      <span className="ml-auto text-[10px] text-muted-foreground tabular-nums">
                        {Math.round(z.canvasWidth)}×{Math.round(z.canvasHeight)} · {z.rotation}°
                      </span>
                      <button
                        type="button"
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={(e) => { e.stopPropagation(); allView.snapshot(); allView.updateZoneRotation(z.id, nextRot); }}
                        className="text-[10px] px-1.5 py-0.5 rounded border hover:bg-muted"
                        title="Rotation (cycle 90°)"
                      >↻</button>
                      <input
                        type="color"
                        value={z.backgroundColor ?? '#ffffff'}
                        onPointerDown={(e) => e.stopPropagation()}
                        onChange={(e) => { allView.snapshot(); allView.updateZoneBackground(z.id, e.target.value); }}
                        className="w-5 h-5 rounded border bg-transparent cursor-pointer"
                        title="Couleur de fond"
                      />
                    </div>
                  ) : (
                    /* Étiquette passive (édition tables + service) */
                    <div className="absolute top-1 left-2 pointer-events-none opacity-70 text-[10px] font-semibold flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: z.color || '#6366f1' }} />
                      <span style={{ color: z.color || undefined }}>{z.name}</span>
                    </div>
                  )}
                  {/* Étape 8 — fixtures de la zone (sous les tables) */}
                  {zoneFixtures.map(f => (
                    <V2Fixture key={f.id} fixture={f} />
                  ))}
                  {/* Tables : rendues ici SAUF en sous-mode édition tables (calque global ci-dessous) */}
                  {!isAllZonesTableEdit && zonePositions.map(p => renderPos(p))}
                  {/* Resize handle (bottom-right corner) — uniquement en sous-mode "Disposer les zones" */}
                  {showZoneChrome && (
                    <div
                      onPointerDown={(e) => startZoneResize(e, z.id, z.canvasWidth, z.canvasHeight)}
                      onPointerMove={onZoneResizePointerMove}
                      onPointerUp={endZoneResize}
                      onPointerCancel={endZoneResize}
                      className="absolute bottom-0 right-0 w-4 h-4 cursor-se-resize bg-primary/40 hover:bg-primary rounded-tl"
                      title="Redimensionner la zone"
                    />
                  )}
                </div>
              );
            });

            // Calque global tables (sous-mode édition tables uniquement)
            const globalTablesLayer = isAllZonesTableEdit ? (
              <div
                key="__all_tables_layer__"
                data-v2-all-tables-layer
                className="absolute inset-0 pointer-events-none"
                style={{ zIndex: 5 }}
              >
                <div className="absolute inset-0 pointer-events-auto" style={{ width: '100%', height: '100%' }}>
                  {positions.map((p) => {
                    if (!p.zoneId) return renderPos(p);
                    const z = allView.zones.find(zz => zz.id === p.zoneId);
                    if (!z) return renderPos(p);
                    const g = globalFromLocal(z as AllViewZoneLite, p.x, p.y);
                    const globalRot = ((p.rotation + z.rotation) % 360 + 360) % 360;
                    return renderPos(p, { x: offX + g.x, y: offY + g.y, rotation: globalRot });
                  })}
                </div>
              </div>
            ) : null;

            return (
              <>
                {zoneWrappers}
                {globalTablesLayer}
              </>
            );
          })()}


          {canEditTables && (
            <>
              <Moveable
                ref={moveableRef}
                target={targets.length === 1 ? targets[0] : targets}
                draggable
                rotatable
                snappable
                origin={false}
                dragArea={true}
                passDragArea
                throttleDrag={0}
                throttleRotate={5}
                snapThreshold={5}
                snapDirections={{ top: true, left: true, bottom: true, right: true, center: true, middle: true }}
                elementSnapDirections={{ top: true, left: true, bottom: true, right: true, center: true, middle: true }}
                elementGuidelines={Array.from(canvasRef.current?.querySelectorAll('[data-v2-table-id]') || [])}
                onDragStart={onDragStart}
                onDrag={onDrag}
                onDragEnd={onDragEnd}
                onRotateStart={onRotateStart}
                onRotate={onRotate}
                onRotateEnd={onRotateEnd}
                onDragGroupStart={(e: any) => {
                  const ids = e.events.map((ev: any) => ev.target.getAttribute('data-v2-table-id')).filter(Boolean);
                  if (!guardTableIdsForEdit(ids)) { e.stop?.(); return false; }
                  setIsDraggingTables(true);
                  snapshot();
                  const inp = e.inputEvent as MouseEvent;
                  captureDragStarts(
                    e.events.map((ev: any) => ev.target),
                    inp?.clientX ?? 0,
                    inp?.clientY ?? 0,
                  );
                }}
                onDragGroup={(e: any) => {
                  const inp = e.inputEvent as MouseEvent | undefined;
                  const cx = inp?.clientX ?? e.clientX;
                  const cy = inp?.clientY ?? e.clientY;
                  if (cx == null || cy == null) return;
                  const z = zoom || 1;
                  let dx = (cx - dragRef.current.startClientX) / z;
                  let dy = (cy - dragRef.current.startClientY) / z;
                  // Bound delta at the group level so tables don't squash against canvas edges
                  let minX = Infinity, minY = Infinity, maxRight = -Infinity, maxBottom = -Infinity;
                  dragRef.current.starts.forEach(s => {
                    minX = Math.min(minX, s.x);
                    minY = Math.min(minY, s.y);
                    maxRight = Math.max(maxRight, s.x + s.w);
                    maxBottom = Math.max(maxBottom, s.y + s.h);
                  });
                  if (Number.isFinite(minX)) {
                    // Only clamp on left/top; right/bottom grows the canvas instead.
                    dx = Math.max(-minX, dx);
                    dy = Math.max(-minY, dy);
                    const newRight = maxRight + dx;
                    const newBottom = maxBottom + dy;
                    if (newRight > canvasW - CANVAS_MARGIN / 2) {
                      const next = Math.max(canvasW, snap(newRight + CANVAS_MARGIN));
                      if (next > canvasW) { setAutoCanvasW(next); flashNotice(next, canvasH); }
                    }
                    if (newBottom > canvasH - CANVAS_MARGIN / 2) {
                      const next = Math.max(canvasH, snap(newBottom + CANVAS_MARGIN));
                      if (next > canvasH) { setAutoCanvasH(next); flashNotice(canvasW, next); }
                    }
                  }
                  e.events.forEach((ev: any) => {
                    const id = ev.target.getAttribute('data-v2-table-id');
                    const start = id && dragRef.current.starts.get(id);
                    if (!start) return;
                    const nx = snap(start.x + dx);
                    const ny = snap(start.y + dy);
                    ev.target.style.left = `${nx}px`;
                    ev.target.style.top = `${ny}px`;
                    ev.target.dataset.tx = String(nx);
                    ev.target.dataset.ty = String(ny);
                  });
                }}
                onDragGroupEnd={(e: any) => {
                  setIsDraggingTables(false);
                  const updates = new Map<string, { x: number; y: number }>();
                  e.events.forEach((ev: any) => {
                    const id = ev.target.getAttribute('data-v2-table-id');
                    if (!id) return;
                    const tx = ev.target.dataset.tx;
                    const ty = ev.target.dataset.ty;
                    if (tx != null && ty != null) {
                      updates.set(id, { x: Number(tx), y: Number(ty) });
                    }
                    delete ev.target.dataset.tx;
                    delete ev.target.dataset.ty;
                  });
                  if (updates.size === 0) return;
                  updateMany(prev => prev.map(p => {
                    const u = updates.get(p.tableId);
                    return u ? { ...p, x: u.x, y: u.y } : p;
                  }));
                }}
                onRotateGroupStart={(e: any) => {
                  const ids = e.events.map((ev: any) => ev.target.getAttribute('data-v2-table-id')).filter(Boolean);
                  if (!guardTableIdsForEdit(ids)) { e.stop?.(); return false; }
                  snapshot();
                }}
                onRotateGroup={(e: any) => {
                  e.events.forEach((ev: any) => {
                    const id = ev.target.getAttribute('data-v2-table-id');
                    const pos = positions.find(p => p.tableId === id);
                    if (!pos) return;
                    ev.target.style.transform = `rotate(${ev.rotate}deg)`;
                    ev.target.dataset.tr = String(ev.rotate);
                  });
                }}
                onRotateGroupEnd={(e: any) => {
                  const updates = new Map<string, number>();
                  e.events.forEach((ev: any) => {
                    const id = ev.target.getAttribute('data-v2-table-id');
                    if (!id) return;
                    const tr = ev.target.dataset.tr;
                    if (tr != null) updates.set(id, Number(tr));
                    delete ev.target.dataset.tr;
                  });
                  if (updates.size === 0) return;
                  updateMany(prev => prev.map(p => {
                    const r = updates.get(p.tableId);
                    return r != null ? { ...p, rotation: r } : p;
                  }));
                }}
              />
              <Selecto
                container={canvasRef.current}
                rootContainer={containerRef.current}
                dragContainer={containerRef.current}
                boundContainer={containerRef.current}
                selectableTargets={['[data-v2-table-id]']}
                hitRate={5}
                selectByClick={false}
                selectFromInside={false}
                continueSelect={false}
                toggleContinueSelect={['shift']}
                preventDefault
                dragCondition={(e: any) => {
                  // Only allow Selecto when the gesture starts on the background.
                  // Drags on tables (selected or not) are handled by our pointerdown
                  // capture handler + Moveable.dragStart().
                  const inp = e.inputEvent as MouseEvent;
                  const target = inp?.target as HTMLElement | undefined;
                  if (!target) return true;
                  const tableEl = target.closest('[data-v2-table-id]');
                  if (tableEl) return false;
                  if (moveableRef.current?.isMoveableElement?.(target)) return false;
                  return true;
                }}
                onSelect={(e: any) => {
                  const ids = e.selected
                    .map((el: HTMLElement) => el.getAttribute('data-v2-table-id')!)
                    .filter(Boolean);
                  setSelectedIds(ids);
                }}
              />
            </>
          )}
        </div>

        {canEditTables && (
          <div className="pointer-events-none absolute bottom-2 left-3 max-w-[60%] text-[10px] text-muted-foreground/80 bg-background/70 backdrop-blur px-2 py-1 rounded">
            Cliquer une table = sélection · Glisser une table = déplacer (groupe si multi-sélection) · Fond = lasso · Shift = ajouter · Alt+glisser = pan · ⌘+molette = zoom
          </div>
        )}
        {showAllEdit && arrangeZones && (
          <div className="pointer-events-none absolute bottom-2 left-3 max-w-[70%] text-[10px] text-primary-foreground bg-primary/90 backdrop-blur px-2 py-1 rounded">
            Mode disposition des zones — glisser le titre pour déplacer, coin pour redimensionner, ↻ pour pivoter
          </div>
        )}

        {canEditTables && containerSize.w > 0 && (
          <V2Minimap
            canvasW={canvasW}
            canvasH={canvasH}
            positions={positions}
            pan={pan}
            zoom={zoom}
            containerW={containerSize.w}
            containerH={containerSize.h}
            onRecenter={(cx, cy) => {
              setPan(clampPan({
                x: containerSize.w / 2 - cx * zoom,
                y: containerSize.h / 2 - cy * zoom,
              }, zoom));
            }}
          />
        )}

        {isServiceMode && !selectedTime && (
          <div className="pointer-events-none absolute top-2 left-1/2 -translate-x-1/2 text-xs text-muted-foreground bg-background/80 backdrop-blur px-3 py-1 rounded-full">
            Sélectionne un créneau pour voir l'occupation
          </div>
        )}

        {isAllZones && !isServiceCapable && !canEditTables && (
          <div className="pointer-events-none absolute top-2 left-1/2 -translate-x-1/2 text-xs text-muted-foreground bg-background/80 backdrop-blur px-3 py-1 rounded-full">
            Vue d'ensemble — composition des zones.
          </div>
        )}

        {isEditMode && extendedNotice && (
          <div className="pointer-events-none absolute top-2 left-3 text-xs text-foreground bg-primary/10 border border-primary/30 backdrop-blur px-3 py-1 rounded-full animate-in fade-in slide-in-from-top-1">
            {extendedNotice}
          </div>
        )}
      </div>
      </div>

      {/* C1 — Swap confirmation dialog */}
      <TableSwapConfirmDialog
        open={!!swapDialog}
        onOpenChange={(open) => { if (!open) setSwapDialog(null); }}
        draggedReservation={swapDialog ? {
          id: swapDialog.dragged.id,
          customerName: `${swapDialog.dragged.customerFirstName} ${swapDialog.dragged.customerLastName}`.trim(),
          numberOfPeople: swapDialog.dragged.numberOfPeople,
          tableName: tables.find(t => t.id === swapDialog.dragged.tableId)?.name
            ?? combinedTables.find(c => c.id === swapDialog.dragged.combinedTableId)?.name
            ?? '—',
          tableId: swapDialog.dragged.tableId ?? null,
          combinedTableId: swapDialog.dragged.combinedTableId ?? null,
        } : null}
        targetReservation={swapDialog ? {
          id: swapDialog.target.id,
          customerName: `${swapDialog.target.customerFirstName} ${swapDialog.target.customerLastName}`.trim(),
          numberOfPeople: swapDialog.target.numberOfPeople,
          tableName: tables.find(t => t.id === swapDialog.target.tableId)?.name ?? '—',
          tableId: swapDialog.target.tableId ?? null,
          combinedTableId: swapDialog.target.combinedTableId ?? null,
        } : null}
        onConfirm={confirmSwap}
      />

      {/* P0-3 — Soft-blocker force dialog (capacity/children/stroller) */}
      <ForceDropDialog
        open={!!forceDialog}
        blockers={forceDialog?.blockers ?? []}
        tableName={forceDialog?.tableName}
        onConfirm={confirmForceDrop}
        onCancel={() => setForceDialog(null)}
      />

      {/* P0-3 — Floating drag preview (TouchBackend needs this; HTML5 uses it too for consistency) */}
      <ReservationDragLayer reservationsById={reservationsById} />

      {/* Étape 7 — palette de recherche ⌘K */}
      <V2TableSearchPalette
        open={searchOpen}
        onOpenChange={setSearchOpen}
        tables={tables}
        zones={zones}
        tableDayReservations={tableDayReservationsMap}
        dayReservations={dayReservationsForDate}
        selectedDate={selectedDate}
        onSelect={(tableId, targetZoneId, reservationId) => {
          const reservation = reservationId ? reservationsById.get(reservationId) : undefined;

          // No table → just open the preview (en_attente case).
          if (!tableId) {
            if (reservation && onPreviewReservation) onPreviewReservation(reservation);
            return;
          }

          // Switch zone if needed
          if (targetZoneId && targetZoneId !== selectedZoneId) {
            setSelectedZoneId(targetZoneId);
          }
          if (reservationId) setHighlightedReservationId(reservationId);
          setFocusedTableId(tableId);
          // Scroll into view after the canvas has re-rendered for the target zone
          requestAnimationFrame(() => {
            setTimeout(() => {
              const node = document.querySelector(
                `[data-v2-table-id="${tableId}"]`
              ) as HTMLElement | null;
              node?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
            }, 60);
          });

          // If the picked row was a client → also open the preview dialog.
          if (reservation && onPreviewReservation) {
            setTimeout(() => onPreviewReservation(reservation), 80);
          }
        }}

      />

    </div>

    </TooltipProvider>
  );
};
